import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faTimes, faWindowMaximize, faWindowMinimize, faWindowRestore } from '@fortawesome/pro-regular-svg-icons';
import { Select, Store } from '@ngxs/store';
import { WindowState } from '@progress/kendo-angular-dialog';
import { Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';
import { CommunicationFailureSelectors } from 'src/app/store/server-communication/communication-failure.selectors';
import {
   CreateWindow,
   HideWindow,
   MaximizeWindow,
   RestoreWindow,
   SetWindowDraggedOrResized,
   SetWindowHeight,
   SetWindowIndex,
   SetWindowLeft,
   SetWindowTop,
   SetWindowWidth,
} from 'src/app/store/utility/samweb-window/samweb-window.actions';
import { SamwebWindowState, WindowType } from 'src/app/store/utility/samweb-window/samweb-window.state';
import { fadeIntoExistance, windowHideShow } from '../animation/animations';

@Component({
   selector: 'samweb-window',
   templateUrl: './samweb-window.component.html',
   styleUrls: ['./samweb-window.component.scss'],
   animations: [fadeIntoExistance, windowHideShow],
})
export class SamwebWindowComponent implements OnInit, OnDestroy, AfterViewInit {
   private destroy$ = new Subject();

   @Input()
   public id: string;

   @Input()
   public type: WindowType;

   @Input()
   public titleBarIcon: IconDefinition;

   @Input()
   public titleBarHeader: string;

   @Input()
   public titleBarAdditionalInfo: string;

   @Output()
   public cancel = new EventEmitter();

   public WindowHideIcon: IconDefinition = faWindowMinimize;
   public WindowMaximizeIcon: IconDefinition = faWindowMaximize;
   public WindowRestoreIcon: IconDefinition = faWindowRestore;
   public WindowCloseIcon: IconDefinition = faTimes;

   @Select(CommunicationFailureSelectors.isCommunicationHealthy)
   isCommunicationHealthy$: Observable<boolean>;

   public windowState$: Observable<WindowState>;

   public isOpen$: Observable<boolean>;

   public zIndex$: Observable<number>;

   public left: number;

   public top: number;

   public width: number;

   public height: number;

   public isInitialized = false;

   @ViewChild('closePopupControl', { read: ElementRef })
   public closePopupControl: ElementRef;

   constructor(public store: Store) {}

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   ngAfterViewInit(): void {
      const index = this.store.selectSnapshot(SamwebWindowState.getHighestIndex);
      this.store.dispatch(new SetWindowIndex(this.type, this.id, index + 1));

      // Update window size to store, because we have not defaults.
      this.store.dispatch(new SetWindowTop(this.type, this.id, this.top));
      this.store.dispatch(new SetWindowLeft(this.type, this.id, this.left));
      this.store.dispatch(new SetWindowHeight(this.type, this.id, this.height));
      this.store.dispatch(new SetWindowWidth(this.type, this.id, this.width));
      this.isInitialized = true;
   }

   ngOnInit() {
      console.debug('Create Window', this.type, this.id);
      this.store.dispatch(new CreateWindow(this.type, this.id));
      this.refreshWindowSize();
      this.windowState$ = this.store.select(SamwebWindowState.getWindowState).pipe(map((windowState) => windowState(this.type, this.id)));
      this.isOpen$ = this.store.select(SamwebWindowState.isOpen).pipe(map((isOpen) => isOpen(this.type, this.id)));
      this.zIndex$ = this.store.select(SamwebWindowState.getIndexValue).pipe(map((getIndexValue) => getIndexValue(this.type, this.id)));

      combineLatest([
         this.store.select(SamwebWindowState.getTopValue).pipe(map((getValue) => getValue(this.type, this.id))),
         this.store.select(SamwebWindowState.getLeftValue).pipe(map((getValue) => getValue(this.type, this.id))),
         this.store.select(SamwebWindowState.getHeightValue).pipe(map((getValue) => getValue(this.type, this.id))),
         this.store.select(SamwebWindowState.getWidthValue).pipe(map((getValue) => getValue(this.type, this.id))),
      ])
         .pipe(
            distinctUntilChanged(),
            debounceTime(50),
            tap(([top, left, height, width]) => {
               this.top = top;
               this.left = left;
               this.height = height;
               this.width = width;
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   refreshWindowSize() {
      // Get Window Size
      this.top = this.store.selectSnapshot(SamwebWindowState.getTopValue)(this.type, this.id);
      this.left = this.store.selectSnapshot(SamwebWindowState.getLeftValue)(this.type, this.id);
      this.height = this.store.selectSnapshot(SamwebWindowState.getHeightValue)(this.type, this.id);
      this.width = this.store.selectSnapshot(SamwebWindowState.getWidthValue)(this.type, this.id);
   }

   onStateChange(state: WindowState): void {
      if (state === 'minimized') {
         this.store.dispatch(new HideWindow(this.type, this.id));
      }
      if (state === 'default') {
         this.store.dispatch(new RestoreWindow(this.type, this.id));
      }
      if (state === 'maximized') {
         this.store.dispatch(new MaximizeWindow(this.type, this.id));
      }
   }

   public hideWindow(): void {
      this.store.dispatch(new HideWindow(this.type, this.id));
   }

   public maximizeWindow(): void {
      this.store.dispatch(new MaximizeWindow(this.type, this.id));
   }

   public restoreWindow(): void {
      this.store.dispatch(new RestoreWindow(this.type, this.id));
   }

   public topChange(value: number) {
      this.store.dispatch(new SetWindowTop(this.type, this.id, value));
   }

   public leftChange(value: number) {
      this.store.dispatch(new SetWindowLeft(this.type, this.id, value));
   }

   public heightChange(value: number) {
      this.store.dispatch(new SetWindowHeight(this.type, this.id, value));
   }

   public widthChange(value: number) {
      this.store.dispatch(new SetWindowWidth(this.type, this.id, value));
   }

   onFocus() {
      const index = this.store.selectSnapshot(SamwebWindowState.getHighestIndex);
      this.store.dispatch(new SetWindowIndex(this.type, this.id, index + 1));
   }

   public dragStart() {
      this.store.dispatch(new SetWindowDraggedOrResized(this.type, this.id, true));
   }

   public dragEnd() {
      this.store.dispatch(new SetWindowDraggedOrResized(this.type, this.id, false));
   }

   public resizeStart() {
      this.store.dispatch(new SetWindowDraggedOrResized(this.type, this.id, true));
   }

   public resizeEnd() {
      this.store.dispatch(new SetWindowDraggedOrResized(this.type, this.id, false));
   }

   onCancelClick() {
      this.cancel.emit();
   }
}
